import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, KeyRound, Landmark } from "lucide-react";
import { Shelf, Reveal } from "../Shared";
import { GuideCover } from "../guides/GuideCover";

const API = process.env.REACT_APP_BACKEND_URL;
const TOPICS = new Set(["PR", "Citizenship", "Invest"]);
const RX = /\b(residence|residency|settle|settlement|permanent|\bPR\b|express entry|ILR|indefinite leave|golden visa|naturali[sz]ation|citizenship|points test|189|190)\b/i;
const fmtM = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : "");

export const isResidenceGuide = (p) => TOPICS.has(p.topic) || RX.test(`${p.title} ${(p.keywords || []).join(" ")}`);
const CIT_RX = /\b(citizenship|naturali[sz]ation|naturali[sz]e|passport by descent|dual national|second passport|citizen)\b/i;
export const isCitizenshipGuide = (p) => p.topic === "Citizenship" || CIT_RX.test(`${p.title} ${(p.keywords || []).join(" ")}`);
const VARIANTS = {
  pr: { pick: isResidenceGuide, eyebrow: "Settle for good", title: "Residence & settlement guides", to: "/guides?cat=PR", color: "#B87A00" },
  citizenship: { pick: isCitizenshipGuide, pad: isResidenceGuide, eyebrow: "Become a citizen", title: "Citizenship & naturalisation guides", to: "/guides?cat=Citizenship", color: "#13274d" },
};

let _guides = null, _guidesP = null;
const readSession = () => { try { return JSON.parse(sessionStorage.getItem("vl-guides") || "null"); } catch { return null; } };
export function loadGuides() {
  if (_guides) return Promise.resolve(_guides);
  _guidesP ||= fetch(`${API}/api/blog?type=guide&limit=500`).then((r) => r.json()).then((d) => { _guides = d.items || []; try { sessionStorage.setItem("vl-guides", JSON.stringify(_guides)); } catch {} return _guides; }).catch(() => _guides || []);
  return _guidesP;
}

export function useGuides() {
  const [posts, setPosts] = useState(() => _guides || readSession());
  useEffect(() => {
    let live = true;
    loadGuides().then((items) => live && setPosts(items));
    return () => { live = false; };
  }, []);
  return posts;
}

function GuideShelfCard({ p, i }) {
  return (
    <Reveal delay={i * 0.04} className="w-[240px] shrink-0 snap-start sm:w-[268px]">
      <Link to={`/guides/read/${p.slug}`} className="group block" data-testid={`residence-guide-${p.slug}`}>
        <div className="relative aspect-[4/5] overflow-hidden rounded-card bg-gray6 shadow-card transition-[box-shadow,transform] duration-300 group-hover:-translate-y-1 group-hover:shadow-pop"><GuideCover p={p} /></div>
        <p className="mt-3 line-clamp-2 text-base font-semibold leading-snug text-foreground">{p.title}</p>
        <p className="mt-1 flex items-center gap-1.5 text-sm text-label2"><Clock className="h-3.5 w-3.5" /> {p.read_minutes || 8} min · Updated {fmtM(p.reviewed_at || p.updated_at)}</p>
      </Link>
    </Reveal>
  );
}

export default function ResidenceGuides({ cat = "pr" }) {
  const posts = useGuides();
  const v = VARIANTS[cat] || VARIANTS.pr;
  const pool = (posts || []).filter((p) => p.audience !== "business");
  const primary = pool.filter(v.pick);
  // Citizenship has few dedicated guides yet, pad with residence guides so the shelf never looks empty, citizenship first.
  const list = [...primary, ...(v.pad && primary.length < 4 ? pool.filter((p) => v.pad(p) && !primary.includes(p)) : [])].slice(0, 10);
  if (!primary.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8" data-testid="residence-guides-row" data-variant={cat}>
      <Shelf ariaLabel={v.title}
        title={<div><p className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: v.color }}>{cat === "citizenship" ? <Landmark className="h-3.5 w-3.5" /> : <KeyRound className="h-3.5 w-3.5" />} {v.eyebrow}</p><h2 className="mt-1 font-display text-2xl sm:text-3xl">{v.title}</h2></div>}
        action={<Link to={v.to} className="group inline-flex items-center gap-1 text-sm font-medium text-tint-strong" data-testid="residence-guides-all">See all guides <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></Link>}>
        {list.map((p, i) => <GuideShelfCard key={p.slug} p={p} i={i} />)}
      </Shelf>
    </section>
  );
}
